import { useCallback, useMemo, useState } from "react";
import { gradeAnswers, hitsZone } from "./zones";

// State machine for one "tap the location" task: a list of prompts, one active
// at a time, each answered by a single tap in data coordinates.
//
// mode "guided"   — immediate feedback. A correct tap records the answer and
//                   moves to the next prompt; a wrong tap produces a soft
//                   neutral cue and the prompt stays active. No penalty.
// mode "unguided" — delayed feedback. Any tap is recorded neutrally, the learner
//                   can retap to change it, Submit unlocks once every prompt has
//                   a recorded tap (correct or not), and grading happens only on
//                   submit().
//
// holdAfterCorrect (guided only) — after a correct tap the task stays on that
// prompt, so its explanation is read against the step it belongs to, and moves
// on only when next() is called. Without it, a correct tap advances at once.
export default function useTapLocationTask(prompts, mode, { holdAfterCorrect = false } = {}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [miss, setMiss] = useState(null);
  const [held, setHeld] = useState(null);

  const active = prompts[activeIndex] || null;
  const answeredCount = prompts.filter((p) => answers[p.id]).length;
  const canSubmit = mode === "unguided" && !results && answeredCount === prompts.length;
  const guidedComplete = mode === "guided" && answeredCount === prompts.length;

  const tap = useCallback(
    (pt) => {
      if (!active || results || guidedComplete || held) return null;
      if (mode === "guided") {
        if (!hitsZone(pt, active.zone)) {
          setMiss({ pt, key: Date.now() });
          return { promptId: active.id, correct: false };
        }
        setMiss(null);
        setAnswers((a) => ({ ...a, [active.id]: pt }));
        if (holdAfterCorrect) setHeld(active.id);
        else setActiveIndex((i) => Math.min(i + 1, prompts.length));
        return { promptId: active.id, correct: true };
      }
      const nextAnswers = { ...answers, [active.id]: pt };
      setAnswers(nextAnswers);
      // Move on to the next prompt still waiting for a tap, wrapping around.
      for (let step = 1; step <= prompts.length; step += 1) {
        const i = (activeIndex + step) % prompts.length;
        if (!nextAnswers[prompts[i].id]) {
          setActiveIndex(i);
          break;
        }
      }
      return { promptId: active.id, recorded: true };
    },
    [active, activeIndex, answers, guidedComplete, held, holdAfterCorrect, mode, prompts, results]
  );

  const next = useCallback(() => {
    if (!held) return;
    setHeld(null);
    setActiveIndex((i) => Math.min(i + 1, prompts.length));
  }, [held, prompts.length]);

  const selectPrompt = useCallback(
    (id) => {
      if (mode !== "unguided" || results) return;
      const i = prompts.findIndex((p) => p.id === id);
      if (i >= 0) setActiveIndex(i);
    },
    [mode, prompts, results]
  );

  const submit = useCallback(() => {
    if (!canSubmit) return null;
    const graded = gradeAnswers(prompts, answers);
    setResults(graded);
    return graded;
  }, [answers, canSubmit, prompts]);

  return useMemo(
    () => ({
      active,
      activeIndex,
      answers,
      results,
      miss,
      held,
      canSubmit,
      guidedComplete,
      tap,
      next,
      selectPrompt,
      submit,
    }),
    [active, activeIndex, answers, results, miss, held, canSubmit, guidedComplete, tap, next, selectPrompt, submit]
  );
}
